import { Box, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { CHAT_WIDGET_PUBLIC_KEY } from '../../../config/appConfig';
import AuthContext from '../../../contexes/AuthContext';
import DashboardContext from '../../../contexes/DashboardContext';
import { useSubscription } from '../../../contexes/SubscriptionProvider';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import {
  ChatAgentCreationStep,
  ChatSize,
} from '../../../enums/ChatAgentCreationStep';
import {
  reverseTransformUserDataPrompt,
  transformUserDataPrompt,
} from '../../../helpers/agentHelper';
import { notifyAgentCreated } from '../../../helpers/embedMessaging';
import {
  firstIncompleteChatStep,
  isChatStepComplete,
} from '../../../helpers/chatAgentFormStepCompletion';
import { submitCreateChatAgent } from '../../../helpers/chatAgentFormCreateSubmit';
import { submitUpdateChatAgent } from '../../../helpers/chatAgentFormUpdateSubmit';
import { shouldShowUpgradeCta } from '../../../helpers/billingPlanSwitchUi';
import { shouldBlockNewChatAgent } from '../../../helpers/pricingAgentLimits';
import { useCompanyChatAgents } from '../../../hooks/queries/useCompanyChatAgents';
import {
  parseDigests,
  parseExcludeUrlPatterns,
  parseWebscrapLinks,
  parseWebscrapSourcePage,
  WEBSCRAP_DEFAULT_MAX_PAGES,
  WEBSCRAP_DEFAULT_PAGES_PER_PDF,
} from '../../../helpers/knowledgeHelper';
import { useAnalytics } from '../../../hooks/useAnalytics';
import useCompany from '../../../hooks/useCompany';
import useEmbedMode from '../../../hooks/useEmbedMode';
import useKnowledgeFile from '../../../hooks/useKnowledgeFile';
import { useSnackBar } from '../../../hooks/useSnackBar';
import useStepNavigation from '../../../hooks/useStepNavigation';
import useStepValidationGate from '../../../hooks/useStepValidationGate';
import { useTranslation } from '../../../hooks/useTranslation';
import { AgentStatus } from '../../../interfaces/Agent';
import { WorkflowType } from '../../../interfaces/App';
import {
  AnalyticsCategory,
  ChatAgent,
  ChatAgentFormData,
  UserDataPromptField,
} from '../../../interfaces/ChatAgent';
import Knowledge from '../../../interfaces/Knowledge';
import StyledForm from '../../../styles/StyledForm';
import StyledFormStep from '../../../styles/StyledFormStep';
import AgentKnowledgeStep from '../../forms/AgentKnowledgeStep/AgentKnowledgeStep';
import AgentAnalyticsSettingsStep from '../../forms/AgentAnalyticsSettingsStep/AgentAnalyticsSettingsStep';
import ChatAgentBrandingStep from '../../forms/ChatAgentBrandingStep/ChatAgentBrandingStep';
import ChatAgentFormSettingsStep from '../../forms/ChatAgentFormSettings/ChatAgentFormSettings';
import ChatAgentSettingsStep from '../../forms/ChatAgentSettings/ChatAgentSettings';
import AgentCreationProcessingScreen from '../../shared/AgentCreationProcessingScreen/AgentCreationProcessingScreen';
import AgentCreationSuccessActions from '../../shared/AgentCreationSuccessActions/AgentCreationSuccessActions';
import BillingSupportLinks from '../../shared/BillingSupportLinks/BillingSupportLinks';
import ChatConfigPreview from '../../shared/ChatConfigPreview/ChatConfigPreview';
import CodeSnippet from '../../shared/CodeSnippet/CodeSnippet';
import FreePlanLimitModal from '../../shared/FreePlanLimitModal/FreePlanLimitModal';
import WorkflowFooter from '../../shared/WorkflowFooter/WorkflowFooter';
import WorkflowSidebar from '../../shared/WorkflowSidebar/WorkflowSidebar';
import AgentCreationSuccessContainer from '../AgentForm/AgentCreationSuccessContainer';

interface ChatAgentFormProps {
  chatAgent?: ChatAgent;
  knowledge?: Knowledge;
  workflow?: WorkflowType;
}

const ChatAgentForm: React.FC<ChatAgentFormProps> = ({
  chatAgent,
  knowledge,
  workflow = WorkflowType.CREATE,
}) => {
  const { t } = useTranslation();
  const { currentUser, setCurrentUser } = useContext(AuthContext);
  const { agentsCount } = useContext(DashboardContext);
  const isEmbed = useEmbedMode();
  const agentCreatedNotified = useRef(false);
  const displayMessage = useSnackBar();
  const queryClient = useQueryClient();
  const { subscription } = useSubscription();
  const { track } = useAnalytics();
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
  const { companyDetails } = useCompany(currentUser);

  // Fetched directly so the free-plan agent limit is enforced even on a fresh
  // visit to this page (DashboardContext.agentsCount is only populated as a
  // side-effect of the agents list table mounting).
  const { data: existingChatAgents, isLoading: isExistingAgentsLoading } =
    useCompanyChatAgents(workflow === WorkflowType.CREATE);
  const existingAgentsCount = existingChatAgents?.length ?? agentsCount;

  const stepEnumOrder = [
    ChatAgentCreationStep.BRANDING,
    ChatAgentCreationStep.FORM_SETTINGS,
    ChatAgentCreationStep.SETTINGS,
    ChatAgentCreationStep.ANALYTICS_SETTINGS,
    ChatAgentCreationStep.KNOWLEDGE,
  ];

  const {
    isKnowledgeFilesLoading,
    knowledgeFiles,
    knowledgeFilesData,
    pendingDeletions,
    executeStagedDeletions,
    markKnowledgeFileForDeletion,
    unmarkKnowledgeFileForDeletion,
    handleDownloadKnowledgeFile,
    setIsKnowledgeFilesLoading,
    setKnowledgeFilesData,
    clearKnowledgeFilesData,
    clearIndexDBFiles,
  } = useKnowledgeFile(knowledge as Knowledge, 'chat-agent');

  const steps: JSX.Element[] = [
    <ChatAgentBrandingStep
      key={0}
      stepLabel={t(`Step {{current}} of {{total}}`, {
        current: 1,
        total: stepEnumOrder.length,
      })}
      description={t(
        "This lets you customize your chat assistant's branding and appearance, including avatars, theme color, and chat size."
      )}
    />,
    <ChatAgentFormSettingsStep
      key={1}
      stepLabel={t(`Step {{current}} of {{total}}`, {
        current: 2,
        total: stepEnumOrder.length,
      })}
      description={t(
        'This page allows you to customize your contact form by setting the form title, button label, selecting which user data fields to collect (name, email, company), and adding a disclaimer message.'
      )}
    />,
    <ChatAgentSettingsStep
      key={2}
      stepLabel={t(`Step {{current}} of {{total}}`, {
        current: 3,
        total: stepEnumOrder.length,
      })}
      description={t(
        "This lets you set up your chat agent's basic configuration, including the chat name, title, introduction message, and prompt suggestions for users."
      )}
    />,
    <AgentAnalyticsSettingsStep
      key={3}
      agentType="chat"
      showEmailField={true}
      unknownCategoryEmailFieldName="unknown_category_email"
      stepLabel={t(`Step {{current}} of {{total}}`, {
        current: 4,
        total: stepEnumOrder.length,
      })}
      description={t(
        'This page lets you set up analytics for your chat agent by choosing a summary language and creating categories to organize conversations.'
      )}
    />,
    <AgentKnowledgeStep
      allowExistingKnowledgeSelection
      hasAttachedKnowledge={!!knowledge}
      currentUserId={currentUser?.id}
      pendingDeletions={pendingDeletions}
      deleteKnowledgeFileCallback={markKnowledgeFileForDeletion}
      unmarkKnowledgeFileForDeletion={unmarkKnowledgeFileForDeletion}
      downloadKnowledgeFileCallback={handleDownloadKnowledgeFile}
      description={t(
        'Upload here some files from which your agent will be based to answer user questions during a chat conversations. Do not forget that those information has to be public because they will be shared with your users.'
      )}
      key={4}
      knowledgeFiles={knowledgeFiles}
      knowledgeFilesData={knowledgeFilesData}
      label={
        knowledge ? t('Edit Chat Agent Knowledge') : t('Chat Agent Knowledge')
      }
      setKnowledgeFilesData={setKnowledgeFilesData}
      stepLabel={t(`Step {{current}} of {{total}}`, {
        current: 5,
        total: stepEnumOrder.length,
      })}
      setIsKnowledgeFilesLoading={setIsKnowledgeFilesLoading}
      isKnowledgeFilesLoading={isKnowledgeFilesLoading}
      showWebscrapConfig
      knowledgeId={knowledge?.id}
    />,
  ];

  const [isProcessing, setIsProcessing] = useState(false);
  const [isAgentCreated, setIsAgentCreated] = useState(false);
  const [isKnowledgeCreated, setIsKnowledgeCreated] = useState(false);
  const [isFilesUploaded, setIsFilesUploaded] = useState(false);
  const [isAgentCreationCompleted, setIsAgentCreationCompleted] =
    useState(false);
  const [newAgent, setNewAgent] = useState<any>(null);

  const navigate = useNavigate();
  const { currentStep, goToStep, goToNextStep, goToPreviousStep } =
    useStepNavigation({
      stepEnumOrder,
    });

  const {
    custom_initialization,
    name,
    analytics_language,
    analytics_categories,
  } = chatAgent ?? {};
  const {
    header_text,
    chat_avatar_url,
    header_avatar_url,
    labels_to_translate,
    theme_color,
    size,
    user_data_prompt,
    user_data_prompt_html_disclaimer,
    prompt_suggestions,
  } = custom_initialization ?? {};
  const { user_data_prompt_title, user_data_prompt_button, bubble_text } =
    labels_to_translate ?? {};

  const methods = useForm<ChatAgentFormData>({
    mode: 'onChange',
    defaultValues: {
      // Branding
      header_avatar_url: header_avatar_url ?? '',
      header_avatar_url_filename: '',
      chat_avatar_url: chat_avatar_url ?? '',
      chat_avatar_url_filename: '',
      theme_color: theme_color ?? '#1741ff', // Elora blue
      size: size ?? ChatSize.SMALL,
      // Form Settings
      user_data_prompt_title: user_data_prompt_title ?? '', // form title
      user_data_prompt_button: user_data_prompt_button ?? '', // form button label
      user_data_prompt:
        reverseTransformUserDataPrompt(
          user_data_prompt as UserDataPromptField[]
        ) ?? [], // form user data
      user_data_prompt_html_disclaimer: user_data_prompt_html_disclaimer ?? '', // form disclaimer
      // Settings
      name: name ?? '', // chat name
      header_text: header_text ?? '', // chat title
      bubble_text: bubble_text ?? '', // bubble above chat widget
      start_message: chatAgent?.start_message ?? '', // agent's first message when chat opens
      prompt_suggestions: prompt_suggestions ?? [],
      // Analytics
      analytics_language: analytics_language ?? 'english', // summaries language
      unknown_category_email:
        analytics_categories?.find(
          (c: AnalyticsCategory) => c.name === 'Unknown'
        )?.email ?? '',
      analytics_categories: (() => {
        const filtered = analytics_categories?.filter(
          (c: AnalyticsCategory) => c.name !== 'Unknown'
        );
        return filtered?.length
          ? filtered.map((category: AnalyticsCategory) => ({
              name: category.name,
              description: category.description,
              email: category.email ?? '',
            }))
          : [{ name: '', description: '', email: '' }];
      })(),
      // Knowledge Base
      knowledge_name: '',
      knowledge_language: 'en',
      knowledge_ids: [],
      existing_knowledge_id: '',
      webscrap_enabled: false,
      webscrap_frequency: 'weekly',
      webscrap_source_url: '',
      webscrap_use_sitemap: true,
      webscrap_max_depth: 1,
      webscrap_max_pages: WEBSCRAP_DEFAULT_MAX_PAGES,
      webscrap_pages_per_pdf: WEBSCRAP_DEFAULT_PAGES_PER_PDF,
      webscrap_links: [] as { url: string; depth?: number }[],
      webscrap_exclude_url_patterns: [] as { value: string }[],
      webscrap_scrape_images: false,
      webscrap_digests: [],
      // Others
      status: AgentStatus.ONLINE,
    },
  });

  // Update company field when companyDetails loads
  useEffect(() => {
    if (companyDetails?.name) {
      methods.setValue('company', companyDetails.name);
    }
  }, [companyDetails, methods]);

  // Set default unknown category email from company details (only for CREATE workflow)
  useEffect(() => {
    if (
      workflow === WorkflowType.CREATE &&
      companyDetails?.email &&
      !methods.getValues('unknown_category_email')
    ) {
      methods.setValue('unknown_category_email', companyDetails.email);
    }
  }, [companyDetails, workflow, methods]);

  // Reset form with chatAgent and knowledge data for EDIT workflow
  useEffect(() => {
    if (workflow === WorkflowType.EDIT && chatAgent) {
      const webscrapSource = parseWebscrapSourcePage(
        knowledge?.webscrap?.source_page,
        knowledge?.webscrap?.max_depth
      );
      methods.reset({
        // Branding
        header_avatar_url:
          chatAgent.custom_initialization?.header_avatar_url ?? '',
        header_avatar_url_filename: '',
        chat_avatar_url: chatAgent.custom_initialization?.chat_avatar_url ?? '',
        chat_avatar_url_filename: '',
        theme_color: chatAgent.custom_initialization?.theme_color ?? '#1741ff',
        size: chatAgent.custom_initialization?.size ?? ChatSize.SMALL,
        // Form Settings
        user_data_prompt_title:
          chatAgent.custom_initialization?.labels_to_translate
            ?.user_data_prompt_title ?? '',
        user_data_prompt_button:
          chatAgent.custom_initialization?.labels_to_translate
            ?.user_data_prompt_button ?? '',
        user_data_prompt:
          reverseTransformUserDataPrompt(
            chatAgent.custom_initialization
              ?.user_data_prompt as UserDataPromptField[]
          ) ?? [],
        user_data_prompt_html_disclaimer:
          chatAgent.custom_initialization?.user_data_prompt_html_disclaimer ??
          '',
        // Settings
        name: chatAgent.name ?? '',
        header_text: chatAgent.custom_initialization?.header_text ?? '',
        bubble_text:
          chatAgent.custom_initialization?.labels_to_translate?.bubble_text ??
          '',
        start_message: chatAgent.start_message ?? '',
        prompt_suggestions:
          chatAgent.custom_initialization?.prompt_suggestions ?? [],
        // Analytics
        analytics_language: chatAgent.analytics_language ?? 'english',
        unknown_category_email:
          chatAgent.analytics_categories?.find(
            (c: AnalyticsCategory) => c.name === 'Unknown'
          )?.email ??
          companyDetails?.email ??
          '',
        analytics_categories: (() => {
          const filtered = chatAgent.analytics_categories?.filter(
            (c: AnalyticsCategory) => c.name !== 'Unknown'
          );
          return filtered?.length
            ? filtered.map((category: AnalyticsCategory) => ({
                name: category.name,
                description: category.description,
                email: category.email ?? '',
              }))
            : [{ name: '', description: '', email: '' }];
        })(),
        // Knowledge Base
        knowledge_name: knowledge?.name ?? '',
        knowledge_language: knowledge?.language ?? 'en',
        knowledge_ids: chatAgent.knowledge_ids ?? [],
        // An agent without a knowledge base (or whose one can't be loaded)
        // opens on "No knowledge base"; otherwise on editing the attached one.
        knowledge_mode: knowledge ? 'create' : 'none',
        webscrap_enabled: !!knowledge?.webscrap?.enabled,
        webscrap_frequency: knowledge?.webscrap?.frequency ?? 'weekly',
        webscrap_source_url: webscrapSource.url,
        webscrap_use_sitemap: webscrapSource.useSitemap,
        webscrap_max_depth: webscrapSource.maxDepth,
        webscrap_max_pages:
          knowledge?.webscrap?.max_pages ?? WEBSCRAP_DEFAULT_MAX_PAGES,
        webscrap_pages_per_pdf:
          knowledge?.webscrap?.pages_per_pdf ?? WEBSCRAP_DEFAULT_PAGES_PER_PDF,
        webscrap_links: parseWebscrapLinks(knowledge?.webscrap?.links),
        webscrap_exclude_url_patterns: parseExcludeUrlPatterns(
          knowledge?.webscrap?.exclude_url_patterns
        ),
        webscrap_scrape_images: !!knowledge?.webscrap?.scrape_images,
        webscrap_digests: parseDigests(knowledge?.webscrap?.digests),
        // Others
        status: chatAgent.status ?? AgentStatus.ONLINE,
      });
    }
  }, [chatAgent, knowledge, workflow, methods]);

  const navigateToAgents = () => {
    navigate('/agents?tab=chat');
  };

  const navigateToPricing = () => {
    track(AnalyticsEvents.CHOOSE_PLAN_BUTTON_CLICKED);
    setShowLeaveConfirmation(false);
    navigate('/pricing');
  };

  const { formState } = methods;
  const { dirtyFields } = formState;

  const isStep2FieldsDirty =
    !!dirtyFields.user_data_prompt_title ||
    !!dirtyFields.user_data_prompt_button ||
    !!dirtyFields.user_data_prompt ||
    !!dirtyFields.user_data_prompt_html_disclaimer;

  const workflowSidebarSteps = [
    {
      title: t('Chat Agent Branding'),
      description: t(
        "This lets you customize your chat assistant's appearance."
      ),
    },
    {
      title: t('Chat Agent Form Settings'),
      description: t('Customize your contact form fields and disclaimer.'),
    },
    {
      title: t('Chat Agent Settings'),
      description: t("Set your chat agent's base behavior and prompts."),
    },
    {
      title: t('Analytics Settings'),
      description: t('Configure categories and summary language.'),
    },
    {
      title: t('Chat Agent Knowledge'),
      description: t('Attach files and data for your assistant answers.'),
    },
  ];

  // Live, non-blocking per-step completeness for the sidebar warning dot —
  // derived from watched form values so it reflects steps the user hasn't
  // visited yet. Mirrors AgentForm's incompleteSteps; see
  // docs/specs/agent-form-click-to-jump-navigation.md.
  const watchedValues = methods.watch();
  const incompleteSteps = stepEnumOrder.map(
    stepEnum =>
      !isChatStepComplete(stepEnum, watchedValues as ChatAgentFormData)
  );

  // Shared Save-time full-validation gate — see
  // docs/specs/agent-form-click-to-jump-navigation.md. Jumps to the first
  // incomplete step and, once it has mounted, triggers RHF validation, the
  // analytics_categories pairing check, and a toast naming the step.
  const { jumpToIncompleteStep, onInvalidSubmit } = useStepValidationGate<
    ChatAgentCreationStep,
    ChatAgentFormData
  >({
    currentStep,
    goToStep,
    stepEnumOrder,
    applicableStepOrder: stepEnumOrder,
    methods,
    workflowSidebarSteps,
    firstIncompleteStep: firstIncompleteChatStep,
    categoryPairing: {
      step: ChatAgentCreationStep.ANALYTICS_SETTINGS,
      fieldArrayName: 'analytics_categories',
    },
  });

  const onSubmit = async (formData: ChatAgentFormData) => {
    // Check if we're on the last step
    if (currentStep === steps.length - 1) {
      // Save-time full-validation gate — the only remaining validation
      // gate (see docs/specs/agent-form-click-to-jump-navigation.md). Jump
      // to the first incomplete step and abort; the effect above surfaces
      // field errors and a toast once that step has mounted.
      const incompleteStep = firstIncompleteChatStep(formData, stepEnumOrder);
      if (incompleteStep !== undefined) {
        jumpToIncompleteStep(incompleteStep);
        return;
      }

      // By this point the Save-time full-validation gate has already
      // confirmed analytics_categories pairing is complete, so this step
      // only needs to shape the payload: strip empty rows and prepend the
      // Unknown catch-all category.
      const categories = formData.analytics_categories ?? [];
      const nonEmpty = categories.filter(
        cat => cat.name?.trim() || cat.description?.trim()
      );
      formData.analytics_categories = [
        {
          name: 'Unknown',
          description: 'Unknown',
          email: formData.unknown_category_email || '',
        },
        ...nonEmpty,
      ];

      // Handle form submission on the last step
      setIsProcessing(true);

      // Add fields that are not included from the form fields
      formData.user_id = currentUser?.id ?? '';
      formData.user_data_prompt = transformUserDataPrompt(
        formData.user_data_prompt as string[]
      );

      try {
        if (workflow === WorkflowType.EDIT && chatAgent) {
          await submitUpdateChatAgent(formData, {
            methods,
            chatAgent,
            currentUser,
            knowledge,
            knowledgeFilesData,
            executeStagedDeletions,
            queryClient,
            displayMessage,
            t,
            setIsAgentCreated,
            setIsKnowledgeCreated,
            setIsFilesUploaded,
          });
        } else {
          await submitCreateChatAgent(formData, {
            methods,
            currentUser,
            knowledgeFilesData,
            executeStagedDeletions,
            queryClient,
            displayMessage,
            t,
            setIsKnowledgeCreated,
            setIsAgentCreated,
            setIsFilesUploaded,
            setNewAgent,
          });
        }
      } catch (error) {
        console.error('Error submitting chat agent form:', error);
        if (error instanceof Error) {
          displayMessage(error.message, 'error', 8000);
        }
        setIsProcessing(false);
      } finally {
        clearIndexDBFiles();
      }
    } else {
      // Move to the next step
      goToNextStep();
    }
  };

  // Handle agent creation/update completion
  useEffect(() => {
    if (
      workflow === WorkflowType.CREATE &&
      isAgentCreated &&
      isKnowledgeCreated &&
      isFilesUploaded
    ) {
      setIsProcessing(false);
      setIsAgentCreationCompleted(true);
    }

    if (
      workflow === WorkflowType.EDIT &&
      isAgentCreated &&
      isKnowledgeCreated &&
      isFilesUploaded
    ) {
      setIsProcessing(false);
      navigateToAgents();
    }
  }, [isAgentCreated, isKnowledgeCreated, isFilesUploaded]);

  useEffect(() => {
    if (
      !isEmbed ||
      workflow !== WorkflowType.CREATE ||
      !isAgentCreationCompleted ||
      !newAgent?.id ||
      agentCreatedNotified.current
    ) {
      return;
    }

    agentCreatedNotified.current = true;
    notifyAgentCreated({
      agentType: 'chat',
      agentId: newAgent.id,
      agentName: newAgent.name,
    });
  }, [isEmbed, workflow, isAgentCreationCompleted, newAgent]);

  // Chat agent creation success page
  if (workflow === WorkflowType.CREATE && isAgentCreationCompleted) {
    const formData = methods.getValues();
    return (
      <AgentCreationSuccessContainer>
        <Stack
          gap={2.5}
          sx={{ flex: 1, minWidth: 0, maxWidth: 560, margin: '0 auto' }}
        >
          <Stack spacing="1rem" alignItems="center" justifyContent="center">
            <AgentCreationSuccessActions
              congratsMessage={
                newAgent?.name
                  ? t("You've created the chat agent {{name}}!", {
                      name: newAgent?.name,
                    })
                  : t("You've created a new chat agent!")
              }
              onGoToAgents={navigateToAgents}
              showChooseAPlan={shouldShowUpgradeCta(
                companyDetails,
                subscription
              )}
              onChooseAPlan={navigateToPricing}
            />
          </Stack>
          <CodeSnippet
            appId={newAgent?.id || 'their-app-id'}
            publicKey={CHAT_WIDGET_PUBLIC_KEY}
          />
        </Stack>
        <Stack
          flex={1}
          minWidth={0}
          alignItems="center"
          justifyContent="center"
        >
          <ChatConfigPreview
            formData={formData}
            startMessage={formData.start_message}
            showPromptSuggestions={
              (formData.prompt_suggestions?.length ?? 0) > 0
            }
          />
        </Stack>
      </AgentCreationSuccessContainer>
    );
  }

  // Chat agent creation in progress UI
  if (workflow === WorkflowType.CREATE && isProcessing) {
    return (
      <AgentCreationProcessingScreen
        title={t('Chat Agent Creation in Progress')}
        isKnowledgeCreated={isKnowledgeCreated}
        isAgentCreated={isAgentCreated}
        agentCreatedLabel={t('Chat Agent Created')}
        creatingAgentLabel={t('Creating Chat Agent...')}
        isFilesUploaded={isFilesUploaded}
        withKnowledgeBase={methods.getValues('knowledge_mode') !== 'none'}
      />
    );
  }

  if (workflow === WorkflowType.CREATE && isExistingAgentsLoading) {
    return null;
  }

  if (
    workflow === WorkflowType.CREATE &&
    shouldBlockNewChatAgent(companyDetails, existingAgentsCount)
  ) {
    return (
      <FreePlanLimitModal
        onViewPlans={() => navigate('/pricing')}
        onBackToAgents={() => navigate('/agents')}
      />
    );
  }

  return (
    <>
      <FormProvider {...methods}>
        <StyledForm
          onSubmit={methods.handleSubmit(onSubmit, onInvalidSubmit)}
          maxWidth="100%"
          margin="0"
          marginTop="0"
          style={{ alignItems: 'stretch' }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2.5}
            sx={{
              width: '100%',
              maxWidth: 1280,
              mx: 'auto',
              p: { xs: 1.5, md: 2.5 },
              borderRadius: 4,
              bgcolor: '#F6F8FC',
              border: '1px solid rgba(15, 23, 42, 0.06)',
            }}
          >
            <Stack
              spacing={2.5}
              sx={{
                width: { xs: '100%', md: 280 },
                flexShrink: 0,
                justifyContent: 'space-between',
              }}
            >
              <WorkflowSidebar
                title={t('Create your Agent')}
                steps={workflowSidebarSteps}
                currentStep={currentStep}
                incompleteSteps={incompleteSteps}
                onStepClick={goToStep}
              />
              <Box
                sx={{
                  p: 2.5,
                }}
              >
                <BillingSupportLinks />
              </Box>
            </Stack>
            <Stack sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
              <StyledFormStep
                width={
                  currentStep === 0 || currentStep === 2
                    ? 'fit-content'
                    : currentStep === 4
                      ? '500px'
                      : '768px'
                }
                maxWidth={
                  currentStep === 0 || currentStep === 2
                    ? '100%'
                    : currentStep === 4
                      ? '500px'
                      : '768px'
                }
                sx={{
                  bgcolor: '#FFFFFF',
                  borderRadius: 3,
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
                  p: { xs: 2, md: 3 },
                }}
              >
                {steps[currentStep]}
              </StyledFormStep>
            </Stack>
          </Stack>
          <Box sx={{ maxWidth: 1280, width: '100%', mx: 'auto' }}>
            <WorkflowFooter
              currentStep={currentStep}
              isKnowledgeFilesLoading={isKnowledgeFilesLoading}
              isProcessing={isProcessing}
              navigateToAgents={navigateToAgents}
              previousStep={goToPreviousStep}
              steps={steps}
              workflow={workflow}
              isCurrentStepOptional={currentStep === 1 ? true : false}
              isStepDirty={currentStep === 1 ? isStep2FieldsDirty : undefined}
              onNext={goToNextStep}
            />
          </Box>
        </StyledForm>
      </FormProvider>
    </>
  );
};

export default ChatAgentForm;
